// ./src/components/features/CreateEventWizard/components/VenuePickerField.tsx
//
// Picks the event's venue from the account's saved places, reusing the Venue
// entity. "Add a new venue" opens the real VenueEditDrawer in-place (kind=venue);
// the created venue lands in the shared VenuesProvider list and is auto-selected —
// so a first-time user can create their venue without leaving the wizard.
//
// The address + map + open/directions buttons are split into `VenueLocationPanel`
// so a caller can place the map wherever it wants (e.g. beside the fields in the
// details editor). `VenuePickerField` renders the panel inline by default.
//
// Imports VenueEditDrawer from the Venues subfolder barrel (NOT the top-level
// features barrel) to avoid a circular import with CreateEventWizard.

import React, { useMemo, useState } from 'react';
import { Stack, Text, Loader, Group, Button, Box } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useVenuesContext } from '@/contexts';
import { Venue, VENUE_KIND, VenueAddress } from '@/utils/api';
import { AppIcon, LocationPreview, SelectField } from '@/components/generic';
import { VenueEditDrawer } from '@/components/features/Venues';
import { useCreateInPlace } from '@/hooks/useCreateInPlace';
import { CREATE_IN_OWN_ACCOUNT } from '@/utils/createInPlace';

/** One-line "street, city, state" from a venue address (null when empty). */
export const formatVenueAddress = (address?: VenueAddress | null): string | null => {
  if (!address) return null;
  const parts = [address.streetAddress, address.city, address.stateProvince].filter(
    (part): part is string => !!part && part.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(', ') : null;
};

// =============================================================================
// VenueLocationPanel — address + static map + open/directions buttons for a
// venue (looked up by id from the shared VenuesProvider). Renders `emptyHint`
// (or nothing) when there's no venue / no coordinates.
// =============================================================================

export interface VenueLocationPanelProps {
  venueId: string | null;
  /** Shown as a dashed placeholder when no venue/location is available. */
  emptyHint?: string;
  /** Static map height in px (default 150). */
  mapHeight?: number;
}

export const VenueLocationPanel: React.FC<VenueLocationPanelProps> = ({
  venueId,
  emptyHint,
  mapHeight = 150,
}) => {
  const { t } = useTranslation(['events']);
  const { venues } = useVenuesContext();

  const venue = useMemo(() => venues.find((v) => v.id === venueId) ?? null, [venues, venueId]);
  const address = formatVenueAddress(venue?.primaryAddress);
  const lat = venue?.primaryAddress?.latitude ?? null;
  const lng = venue?.primaryAddress?.longitude ?? null;
  const hasCoords = typeof lat === 'number' && typeof lng === 'number';

  const openMap = () => {
    if (!hasCoords) return;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank', 'noopener,noreferrer');
  };
  const openDirections = () => {
    if (!hasCoords) return;
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  if (!venue || (!address && !hasCoords)) {
    if (!emptyHint) return null;
    return (
      <Box
        style={{
          height: mapHeight + 40,
          border: '1px dashed var(--mantine-color-gray-3)',
          borderRadius: 'var(--mantine-radius-md)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          color: 'var(--mantine-color-gray-5)',
          padding: 'var(--mantine-spacing-md)',
          textAlign: 'center',
        }}
      >
        <AppIcon iconName="MapPin" size={22} />
        <Text size="sm" c="dimmed">
          {emptyHint}
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap="xs">
      {address && (
        <Group gap={6} align="center" wrap="nowrap">
          <AppIcon iconName="MapPin" size={16} />
          <Text size="md">{address}</Text>
        </Group>
      )}
      {hasCoords && (
        <>
          <LocationPreview
            latitude={lat as number}
            longitude={lng as number}
            mapHeight={mapHeight}
            showCoordinates={false}
          />
          <Group gap="xs">
            <Button variant="light" size="md" leftSection={<AppIcon iconName="MapPin" size={14} />} onClick={openMap}>
              {t('events:wizard.where.openMap', { defaultValue: 'Open map' })}
            </Button>
            <Button
              variant="light"
              size="md"
              leftSection={<AppIcon iconName="Route" size={14} />}
              onClick={openDirections}
            >
              {t('events:wizard.where.getDirections', { defaultValue: 'Get directions' })}
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
};

// =============================================================================
// VenuePickerField — the venue Select (+ in-place create). Optionally renders
// the location panel inline (default true).
// =============================================================================

export interface VenuePickerFieldProps {
  value: string | null;
  onChange: (venue: Venue | null) => void;
  /** Render the address + map panel under the select (default true). */
  showMap?: boolean;
  /** Server refusal on the venue field — without it a listed error is swallowed. */
  error?: string | null;
}

export const VenuePickerField: React.FC<VenuePickerFieldProps> = ({ value, onChange, showMap = true, error }) => {
  const { t } = useTranslation(['events', 'common']);
  const { venues, loading } = useVenuesContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // #142 — may this person add one here? (never for a crew member: it would land in their OWN account).
  const canCreate = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);

  // Events happen at places, not at vendors (service providers who come to you).
  const places = useMemo(() => venues.filter((venue) => venue.kind !== VENUE_KIND.VENDOR), [venues]);

  const data = useMemo(
    () =>
      places
        .filter((venue) => !!venue.id)
        .map((venue) => ({
          value: venue.id as string,
          label: venue.name || t('events:wizard.where.untitledVenue', { defaultValue: 'Untitled venue' }),
        })),
    [places, t],
  );

  const handleSelect = (id: string | null) => {
    onChange(places.find((venue) => venue.id === id) ?? null);
  };

  const handleCreated = (venue: Venue) => {
    setDrawerOpen(false);
    onChange(venue);
  };

  return (
    <Stack gap="xs">
      {/* SelectField opens the full-screen picker on every phone — this one carries a create action that a cramped
          dropdown would bury. */}
      <SelectField
        label={t('events:wizard.where.venue', { defaultValue: 'Venue' })}
        placeholder={
          loading
            ? t('common:loading', { defaultValue: 'Loading…' })
            : t('events:wizard.where.venuePlaceholder', { defaultValue: 'Pick a venue' })
        }
        data={data}
        value={value}
        onChange={(_name, id) => handleSelect(id)}
        createLabel={canCreate ? t('events:wizard.where.addVenue', { defaultValue: 'Add a new venue' }) : undefined}
        onCreate={canCreate ? () => setDrawerOpen(true) : undefined}
        searchable
        clearable
        nothingFoundMessage={t('events:wizard.where.noVenues', { defaultValue: 'No venues yet — add one below' })}
        leftSection={<AppIcon iconName="MapPin" size={16} />}
        rightSection={loading ? <Loader size="md" /> : undefined}
        size="md"
        error={error || undefined}
      />

      {showMap && <VenueLocationPanel venueId={value} mapHeight={110} />}

      <VenueEditDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        kind={VENUE_KIND.VENUE}
        onCreated={handleCreated}
      />
    </Stack>
  );
};
